export type TwinPlanId = "core" | "scale" | "enterprise";

export type TwinPlan = {
  id: TwinPlanId;
  name: string;
  eyebrow: string;
  price: string;
  cadence?: string;
  monthlyPrice: number;
  description: string;
  detail: string;
};

export type CreditPack = {
  id: string;
  name: string;
  shortName: string;
  price: string;
  monthlyPrice: number;
  credits: number;
  description: string;
};

export const TWIN_PLANS = [
  {
    id: "core",
    name: "Core",
    eyebrow: "Start here",
    price: "From £2,000",
    cadence: "/ month",
    monthlyPrice: 2000,
    description:
      "Your Twin for one focused team, connecting the records and business rules behind its work.",
    detail: "Managed deployment, standard onboarding and product support.",
  },
  {
    id: "scale",
    name: "Scale",
    eyebrow: "Grow adoption",
    price: "From £5,000",
    cadence: "/ month",
    monthlyPrice: 5000,
    description:
      "Bring your Twin to more teams, workflows and systems, with support as you expand.",
    detail: "Pricing reflects your teams, systems and support needs.",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    eyebrow: "Larger organisations",
    price: "From £15,000",
    cadence: "/ month",
    monthlyPrice: 15000,
    description:
      "Your Twin for complex organisations that need tailored controls, hosting and support.",
    detail: "We agree the infrastructure, access rules and support with you.",
  },
] satisfies [TwinPlan, TwinPlan, TwinPlan];

export const CREDIT_PACKS = [
  {
    id: "credits-none",
    name: "No credit pack",
    shortName: "None",
    price: "£0",
    monthlyPrice: 0,
    credits: 0,
    description: "Add model capacity when your first workflow is ready.",
  },
  {
    id: "credits-starter",
    name: "Starter credits",
    shortName: "Starter",
    price: "£250 / month",
    monthlyPrice: 250,
    credits: 25_000,
    description:
      "25,000 credits for one focused workflow or a small group of regular users.",
  },
  {
    id: "credits-team",
    name: "Team credits",
    shortName: "Team",
    price: "£750 / month",
    monthlyPrice: 750,
    credits: 100_000,
    description:
      "100,000 credits for several approved workflows and a wider group of users.",
  },
  {
    id: "credits-scale",
    name: "Scale credits",
    shortName: "Scale",
    price: "£1,800 / month",
    monthlyPrice: 1800,
    credits: 300_000,
    description:
      "300,000 credits for high-volume operations or committed model usage.",
  },
] satisfies [CreditPack, CreditPack, CreditPack, CreditPack];

export const FDE_DAY_RATE = 1200;
export const MAX_FDE_DAYS = 20;

function gbp(amount: number): string {
  return `£${amount.toLocaleString("en-GB")}`;
}

function published<T>(item: T | undefined, what: string): T {
  if (!item) throw new Error(`The pricing data no longer has ${what}.`);
  return item;
}

/** The starting prices other pages quote (the home's start band, the solution pages), from the data above. */
export const STARTING_PRICES = {
  core: gbp(published(TWIN_PLANS.find((plan) => plan.id === "core"), "the Core plan").monthlyPrice),
  credits: gbp(published(CREDIT_PACKS.find((pack) => pack.monthlyPrice > 0), "a paid credit pack").monthlyPrice),
  engineeringDay: gbp(FDE_DAY_RATE),
} as const;

export const FEATURE_ROWS = [
  {
    group: "Twin foundation",
    feature: "A dedicated server for your organisation",
    core: "EU or US region",
    scale: "EU or US region",
    enterprise: "EU or US, tailored environment",
  },
  {
    group: "Twin foundation",
    feature: "Answers from the checked Twin, with sources and limits",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Business information",
    feature: "Approved source connectors, starting read-only",
    core: "Initial source set",
    scale: "More connected systems",
    enterprise: "Agreed system coverage",
  },
  {
    group: "Business information",
    feature: "Twin data refresh",
    core: "Daily scheduled refresh",
    scale: "Higher-frequency refresh",
    enterprise: "Refresh agreed for each source",
  },
  {
    group: "Business information",
    feature: "Historical source import",
    core: "Up to 12 months per source",
    scale: "Extended history",
    enterprise: "History agreed for each source",
  },
  {
    group: "Business information",
    feature: "Business definitions with review and version history",
    core: "Included",
    scale: "Shared across teams",
    enterprise: "Tailored controls",
  },
  {
    group: "Adoption",
    feature: "Team access",
    core: "Up to 10 users",
    scale: "Unlimited users",
    enterprise: "Unlimited users",
  },
  {
    group: "Adoption",
    feature: "Configured models for approved workflows",
    core: "Credit packs",
    scale: "Credit packs",
    enterprise: "Custom model routes",
  },
  {
    group: "Adoption",
    feature: "Third-party AI and developer access",
    core: "Configured access to your Twin",
    scale: "Configured access to your Twin",
    enterprise: "Configured access to your Twin",
  },
  {
    group: "Operations",
    feature: "AI usage and provider costs, where available",
    core: "Included",
    scale: "Included",
    enterprise: "Tailored reporting",
  },
  {
    group: "Operations",
    feature: "Product support",
    core: "Standard",
    scale: "Dedicated support channel",
    enterprise: "Dedicated channel and named service lead",
  },
  {
    group: "Delivery",
    feature: "Optional engineering support",
    core: "Optional",
    scale: "Optional",
    enterprise: "Agreed for your programme",
  },
] as const;
